import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, ShoppingBag, Users, Package, AlertTriangle, Building2, RotateCcw, FileText, Wallet, Store, Trophy, Bell } from "lucide-react";
import api from "../../lib/api";
import { fmtTRY, fmtQty4 } from "../../lib/format";

const Card = ({ icon: Icon, label, value, sub, id, accent }) => (
  <div data-testid={`stat-${id}`} className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm">
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-wider text-stone-400 font-semibold">{label}</p>
      <Icon className={`w-5 h-5 ${accent || "text-forest"}`} />
    </div>
    <p className="text-2xl font-bold text-stone-900 mt-2">{value}</p>
    {sub && <p className="text-xs text-stone-500 mt-1">{sub}</p>}
  </div>
);

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const [channels, setChannels] = useState([]);
  const [best, setBest] = useState(null);
  const [alerts, setAlerts] = useState(null);
  useEffect(() => {
    api.get("/admin/dashboard").then(({ data }) => setD(data)).catch(() => {});
    api.get("/admin/marketplaces").then(({ data }) => setChannels(data)).catch(() => {});
    api.get("/admin/reports/best-sellers/summary").then(({ data }) => setBest(data)).catch(() => {});
    api.get("/admin/stock-alerts/summary").then(({ data }) => setAlerts(data)).catch(() => {});
  }, []);

  if (!d) return <p className="text-stone-500">Yükleniyor...</p>;

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif text-2xl sm:text-3xl text-forest">Merkezi Ticaret Paneli</h1>
      <p className="text-stone-500 text-sm mt-1">Bugün · {new Date().toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric", weekday: "long" })}</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Card id="today-sales" icon={TrendingUp} label="Bugünkü Satış" value={fmtTRY(d.today_sales)} sub={`${d.today_orders} sipariş`} />
        <Card id="month-sales" icon={Wallet} label="Bu Ay Ciro" value={fmtTRY(d.month_sales)} />
        <Card id="total-orders" icon={ShoppingBag} label="Toplam Sipariş" value={d.total_orders} sub={`Ort. sepet ${fmtTRY(d.avg_basket)}`} />
        <Card id="b2b-sales" icon={Building2} label="B2B Satış" value={fmtTRY(d.b2b_sales)} sub={`${d.b2b_pending} bekleyen başvuru`} accent="text-gold-dark" />
        <Card id="products" icon={Package} label="Aktif Ürün" value={d.total_products} sub={`${d.low_stock_count} ürün kritik stokta`} />
        <Card id="customers" icon={Users} label="Müşteri" value={d.total_customers} sub={`Bu ay +${d.new_customers}`} />
        <Card id="pending-orders" icon={ShoppingBag} label="Bekleyen Sipariş" value={d.pending_orders} accent="text-amber-600" />
        <Card id="returns" icon={RotateCcw} label="İade Talebi" value={d.return_requests} accent="text-terra" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
          <h2 className="font-semibold text-stone-900">Son Siparişler</h2>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-stone-400 text-xs uppercase tracking-wider"><th className="pb-2">Sipariş</th><th className="pb-2">Müşteri</th><th className="pb-2">Durum</th><th className="pb-2 text-right">Tutar</th></tr></thead>
              <tbody>
                {d.recent_orders.map((o) => (
                  <tr key={o.order_id} className="border-t border-stone-100">
                    <td className="py-2.5 font-mono text-xs font-semibold">{o.order_number}</td>
                    <td className="py-2.5">{o.address?.full_name}</td>
                    <td className="py-2.5"><span className="text-xs bg-cream-dark rounded-full px-2.5 py-1">{o.status}</span></td>
                    <td className="py-2.5 text-right font-semibold">{fmtTRY(o.total)}</td>
                  </tr>
                ))}
                {d.recent_orders.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-stone-400">Henüz sipariş yok</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
            <h2 className="font-semibold text-stone-900 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" /> Düşük Stok</h2>
            <div className="mt-3 space-y-2">
              {d.low_stock.slice(0, 5).map((p) => (
                <div key={p.product_id} className="flex justify-between text-sm"><span className="text-stone-700 line-clamp-1">{p.name}</span><span className="font-bold text-terra">{fmtQty4(p.stock, p.base_unit || p.unit || "")}</span></div>
              ))}
              {d.low_stock.length === 0 && <p className="text-sm text-stone-400">Kritik stokta ürün yok</p>}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
            <h2 className="font-semibold text-stone-900 flex items-center gap-2"><Store className="w-4 h-4 text-forest" /> Kanal Durumları</h2>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {channels.map((c) => (
                <Link to="/admin/pazaryerleri" key={c.channel_id} className="text-center bg-cream-dark/60 rounded-xl py-2.5 px-1 hover:bg-cream-dark transition-colors">
                  <span className={`inline-block w-2 h-2 rounded-full ${c.status === "active" ? "bg-green-500" : c.status === "pending_auth" ? "bg-amber-500" : "bg-stone-300"}`} />
                  <p className="text-[10px] font-semibold text-stone-700 mt-1 truncate">{c.name}</p>
                </Link>
              ))}
            </div>
          </div>
          {alerts && (alerts.bekleyen > 0 || alerts.hazir > 0) && (
            <div data-testid="dashboard-stock-alerts" className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-stone-900 flex items-center gap-2"><Bell className="w-4 h-4 text-forest" /> Stok Bildirimi Bekleyenler</h2>
                <Link to="/admin/stok-bildirimleri" data-testid="dashboard-stock-alerts-link" className="text-xs font-semibold text-forest hover:underline">Yönet →</Link>
              </div>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-stone-600">Stok bekleyen müşteri</span><span className="font-bold text-amber-600">{alerts.bekleyen}</span></div>
                <div className="flex justify-between"><span className="text-stone-600">Stoğa girdi, bildirim hazır</span><span className="font-bold text-emerald-600">{alerts.hazir}</span></div>
              </div>
              {alerts.demand.length > 0 && (
                <div className="mt-3 pt-3 border-t border-stone-100 space-y-1.5">
                  <p className="text-[11px] uppercase tracking-wider text-stone-400 font-semibold">En çok beklenen ürünler</p>
                  {alerts.demand.slice(0, 4).map((p) => (
                    <div key={p.product_id} className="flex justify-between gap-2 text-sm"><span className="text-stone-700 truncate">{p.product_name}</span><span className="font-bold text-forest shrink-0">{p.count} kişi</span></div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
            <h2 className="font-semibold text-stone-900 flex items-center gap-2"><FileText className="w-4 h-4 text-forest" /> Bekleyen İşler</h2>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-stone-600">B2B başvuruları</span><span className="font-bold">{d.b2b_pending}</span></div>
              <div className="flex justify-between"><span className="text-stone-600">Teklif talepleri</span><span className="font-bold">{d.pending_rfqs}</span></div>
              <div className="flex justify-between"><span className="text-stone-600">Bekleyen siparişler</span><span className="font-bold">{d.pending_orders}</span></div>
              <div className="flex justify-between"><span className="text-stone-600">Toplam KDV (dahil)</span><span className="font-bold">{fmtTRY(d.total_vat)}</span></div>
            </div>
          </div>
        </div>
      </div>

      {best && (
        <div data-testid="dashboard-bestsellers" className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-stone-900 flex items-center gap-2"><Trophy className="w-4 h-4 text-gold-dark" /> Son 30 Günün En Çok Satanları</h2>
              <Link to="/admin/en-cok-satanlar" data-testid="dashboard-bestsellers-link" className="text-xs font-semibold text-forest hover:underline">Raporu Aç →</Link>
            </div>
            <div className="mt-4 space-y-2">
              {best.top10_30g.slice(0, 5).map((p, i) => (
                <div key={p.name} className="flex items-center gap-3 text-sm">
                  <span className="w-6 text-center font-bold text-gold-dark">{i + 1}</span>
                  <span className="flex-1 truncate text-stone-700">{p.name}</span>
                  <span className="font-semibold text-stone-900">{p.net_qty} adet</span>
                  <span className="text-xs text-stone-400">{fmtTRY(p.net_amount)}</span>
                </div>
              ))}
              {best.top10_30g.length === 0 && <p className="text-sm text-stone-400">Son 30 günde satış yok</p>}
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm text-sm space-y-2">
              <h2 className="font-semibold text-stone-900">Satış Özetleri</h2>
              <div className="flex justify-between gap-2"><span className="text-stone-600">Bugünün yıldızı</span><span className="font-bold text-right truncate">{best.today_top?.name || "-"}</span></div>
              <div className="flex justify-between gap-2"><span className="text-stone-600">Ayın şampiyonu</span><span className="font-bold text-right truncate">{best.month_top?.name || "-"}</span></div>
              <div className="flex justify-between gap-2"><span className="text-stone-600">Ciro lideri (ay)</span><span className="font-bold text-right">{best.revenue_top ? fmtTRY(best.revenue_top.amount) : "-"}</span></div>
            </div>
            {best.critical_best.length > 0 && (
              <div data-testid="dashboard-critical-best" className="bg-red-50 border border-red-200 rounded-2xl p-5 text-sm">
                <h3 className="font-semibold text-red-800 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Çok Satan & Kritik Stok</h3>
                <div className="mt-2 space-y-1">{best.critical_best.map((c) => <div key={c.name} className="flex justify-between gap-2"><span className="text-red-700 truncate">{c.name}</span><b className="text-red-800">{fmtQty4(c.stock, c.base_unit || c.unit || "")}</b></div>)}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
